<script lang="ts">
  import closeIcon from "./assets/close.svg?raw";
  import playIcon from "./assets/play.svg?raw";
  import stopIcon from "./assets/stop.svg?raw";
  import pauseIcon from "./assets/pause.svg?raw";
  import loopOnIcon from "./assets/loop-on.svg?raw";
  import loopOffIcon from "./assets/loop-off.svg?raw";

  import { onDestroy, onMount } from "svelte";

  import { soundBoard } from "./store";
  import { stopPropagation } from "./genericHandlers";
  import { fetchAttribution, showToastWrapper, unlinkSound } from "./global";
  import Attribution from "./Attribution.svelte";
  import SoundCardTitle from "./SoundCardTitle.svelte";
  import { Howl } from "howler";
  import type { PlaySettings, SoundCard } from "./SoundCard";
  import { RepeatSettings } from "./lib/repeat";
  import { getOrCreateSoundCache, type SoundCache } from "./lib/soundCache";
  import { htmlDataTypeToHowlerFormat } from "./lib/howler";
  import { notifyWhenMounted } from "./tutorial/Tutorial";

  const FADEOUT_WHEN_STOP_DURATION_MILLIS = 500;

  export let card: SoundCard;
  export let sceneId;
  export let onPlaybackEnd;
  export let onPlayClicked;
  export let onSoundAboutToEnd;
  export let crossfadeDurationMillis;
  export let playSettingsChangedCallback: (
    sceneId,
    cardId,
    mutator: (PlaySettings) => PlaySettings,
  ) => void;
  export let nameChangeCallback;
  export let closeable = true;
  export let sceneVolume;

  let fadeOutTimeout = null;
  let isFadingIn = false;
  let isFadingOut = false;
  let loopOn = false;

  export function play() {
    if (audio === null) {
      return;
    }
    // console.log("BL: Play ", card.name)
    if (isFadingIn) {
      return;
    }
    isPlaying = true;
    setVolume();
    if (fadeOutTimeout !== null) {
      clearTimeout(fadeOutTimeout);
    }
    // @ts-ignore
    audio.play();
  }
  export function fadein(durationMillis) {
    if (audio === null) {
      return;
    }
    // console.log("BL: Schedule Fadein ", card.name)
    if (isFadingIn) {
      // console.log("BL: Already fading in, skipping ", card.name)
      return;
    }
    // console.log("BL: Do Fadein ", card.name)
    if (fadeOutTimeout !== null) {
      clearTimeout(fadeOutTimeout);
    }
    isPlaying = true;
    setVolume();
    const target = audio.volume();
    audio.volume(0);
    // @ts-ignore
    audio.fade(0, target, durationMillis);
    scheduleFade(audio, 0, target, durationMillis);
    // @ts-ignore
    audio.play();
  }
  export function fadeout(durationMillis) {
    if (audio === null) {
      return;
    }
    // console.log("BL: Schedule Fadeout ", card.name)
    if (isFadingOut) {
      // console.log("BL: Already fading out, skipping ", card.name)
      return;
    }
    // console.log("BL: Do Fadeout ", card.name)
    scheduleFade(audio, audio.volume(), 0, durationMillis);
    fadeOutTimeout = setTimeout(() => {
      stop();
    }, durationMillis);
  }
  export function stop() {
    if (audio === null) {
      return;
    }
    // console.log("BL: Stop ", card.name)
    // @ts-ignore
    audio.stop();
    isPlaying = false;
    clearInterval(progessPollInterval);
    updateProgress();
    if (fadeOutTimeout !== null) {
      clearTimeout(fadeOutTimeout);
      fadeOutTimeout = null;
    }
  }

  function scheduleFade(audio, from, to, durationMillis) {
    if (audio === null) {
      return;
    }
    audio.fade(from, to, durationMillis);
    if (from > to) {
      isFadingOut = true;
      setTimeout(() => {
        isFadingOut = false;
      }, durationMillis);
    } else {
      isFadingIn = true;
      setTimeout(() => {
        isFadingIn = false;
      }, durationMillis);
    }
  }

  let audio: Howl | null = null;
  let soundCache: SoundCache | null = null;

  let attribution = null;
  let progressBar;
  let volume;

  onMount(async () => {
    soundCache = await getOrCreateSoundCache();
    const soundUrl: string = await soundCache.fetchSound(card.sound);
    const shouldUseWebAudio: boolean = await soundCache.shouldUseWebAudio(card.sound);
    audio = new Howl({
      src: [soundUrl],
      format: [htmlDataTypeToHowlerFormat(card.sound.html_data_type)],
      // If the sound is in the cache, we use WebAudio, which loops smoothly but requires preloading the sound.
      // Ref: https://stackoverflow.com/a/7520220
      html5: !shouldUseWebAudio,
      onend: handlePlaybackEnded,
      onplay: handleOnAudioPlay,
      onpause: handleOnAudioPause,
      onseek: handleOnTimeUpdate,
    });
    assignAttribution();
    volume.value = card.playSettings.volume;
    setVolume();
    if (card.playSettings && card.playSettings.repeat) {
      const isLooping = card.playSettings.repeat.isLooping();
      audio.loop(isLooping);
      loopOn = isLooping;
    }
  });

  onDestroy(() => {
    if (progessPollInterval) {
      clearInterval(progessPollInterval);
    }
  });

  $: if (audio && volume && sceneVolume !== undefined) {
    const curVolume = volume.valueAsNumber;
    const sceneVolumeMod = sceneVolume / 100;
    audio.volume((curVolume * sceneVolumeMod) / 100);
  }

  function setVolume() {
    if (audio === null) {
      return;
    }
    audio.volume(volume.valueAsNumber / 100);
    if (playSettingsChangedCallback) {
      playSettingsChangedCallback(sceneId, card.id, (old) => {
        old.volume = volume.valueAsNumber;
        return old;
      });
    }
  }

  const assignAttribution = () => {
    fetchAttribution(card.sound.id, (att) => (attribution = att)); // Might be null if there is no attribution
  };

  const deleteCard = () => {
    // @ts-ignore
    stop();
    soundBoard.DeleteCard(sceneId, card["id"]);
    const soundId = card["sound"].id;
    unlinkSound(soundId, $soundBoard);
  };

  const handleNameChangeRequest = (newName) => {
    if (nameChangeCallback) {
      nameChangeCallback(sceneId, card.id, newName);
    }
  };

  let isPlaying = false;
  function handlePlayClicked() {
    // It's very important that we call onPlayClicked before play(),
    // because onPlayClicked might stop() every card, so we make sure we're playing ourselves.
    if (onPlayClicked) {
      onPlayClicked();
    }
    play();
  }
  function handlePauseClicked() {
    isPlaying = false;
    // @ts-ignore
    audio.pause();
  }
  function handleStopClicked() {
    if (!isFadingOut) {
      fadeout(FADEOUT_WHEN_STOP_DURATION_MILLIS);
    } else {
      stop();
    }
  }

  function handlePlaybackEnded() {
    if (audio !== null && audio.loop()) {
      return;
    }
    stop();
    if (onPlaybackEnd) {
      onPlaybackEnd();
    }
  }

  let progessPollInterval = null;
  let updateProgress = () => {
    if (audio === null) {
      return;
    }
    // @ts-ignore
    const duration = audio.duration();
    if (duration == 0) {
      return;
    }
    // @ts-ignore
    const position = audio.seek();
    // @ts-ignore
    let progress = position / duration;
    progress = progress * 100.0;
    progressBar.value = progress;
    // @ts-ignore
    const remainingMillis = (duration - position) * 1000;
    // TODO BL: It's possible that we should actually trigger the onSoundAboutToEnd anyway if we want metrics or something,
    // but honestly it depends on what the future holds.
    if (audio.loop()) {
      return;
    }
    if (remainingMillis < crossfadeDurationMillis) {
      if (onSoundAboutToEnd) {
        onSoundAboutToEnd();
      }
    }
  };

  function handleOnAudioPlay() {
    progessPollInterval = setInterval(() => {
      updateProgress();
    }, 100);
  }
  function handleOnAudioPause() {
    clearInterval(progessPollInterval);
    updateProgress();
  }
  function handleOnTimeUpdate() {
    updateProgress();
  }

  function changeLoop(newLoopValue: boolean) {
    if (audio === null) {
      return;
    }
    audio.loop(newLoopValue);
    loopOn = newLoopValue;
    if (playSettingsChangedCallback) {
      playSettingsChangedCallback(sceneId, card.id, (old) => {
        old.repeat = newLoopValue ? RepeatSettings.LOOP() : null;
        return old;
      });
    }
  }
  function handleLoopOnClicked() {
    changeLoop(false);
  }
  function handleLoopOffClicked() {
    changeLoop(true);
  }

  function handleVolumeChange() {
    setVolume();
  }

  function handleProgressClicked(evt) {
    if (audio !== null) {
      const progressPercent = evt.offsetX / evt.target.offsetWidth;
      audio.seek(progressPercent * audio.duration());
    }
    updateProgress();
  }
</script>

<div id="soundcard-{card['id']}" use:notifyWhenMounted>
  <div class="soundcard-outer-container editable-icon">
    {#if closeable}
      <button
        class="hover-target soundcard-close-button"
        tabindex="0"
        aria-label="remove card"
        on:click={deleteCard}
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      >
        {@html closeIcon}
      </button>
    {/if}
    <div
      id="soundcard-container-{card['id']}"
      class="soundcard-container soundcard soundcard-plain-dimensions"
      class:soundcard-off={!isPlaying}
      class:soundcard-contrast={isPlaying}
    >
      <div class="soundcard-header">
        <SoundCardTitle {card} nameChangeCallback={handleNameChangeRequest} />
      </div>
      <div on:pointerdown={stopPropagation} on:mousedown={stopPropagation}>
        <input
          class="soundcard-volume"
          id="volume-{card['id']}"
          type="range"
          min="0"
          max="100"
          bind:this={volume}
          on:change={handleVolumeChange}
        />
      </div>
      <div
        class="soundcard-controls"
        on:pointerdown={stopPropagation}
        on:mousedown={stopPropagation}
      >
        {#if isPlaying}
          <button
            id="pause-{card['id']}"
            class="soundcard-button"
            type="button"
            on:click={handlePauseClicked}
          >
            {@html pauseIcon}
          </button>
        {:else}
          <button
            id="play-{card['id']}"
            class="soundcard-button"
            type="button"
            on:click={handlePlayClicked}
          >
            {@html playIcon}
          </button>
        {/if}
        <button
          id="stop-{card['id']}"
          class="soundcard-button"
          type="button"
          on:click={handleStopClicked}
        >
          {@html stopIcon}
        </button>

        {#if loopOn}
          <button
            id="loop-on-{card['id']}"
            class="soundcard-button soundcard-contrast"
            type="button"
            on:click={handleLoopOnClicked}
          >
            {@html loopOnIcon}
          </button>
        {:else}
          <button
            id="loop-off-{card['id']}"
            class="soundcard-button blue"
            type="button"
            on:click={handleLoopOffClicked}
          >
            {@html loopOffIcon}
          </button>
        {/if}
      </div>
      <div class="soundcard-attribution">
        {#if attribution}
          <Attribution {attribution} isUserSound={false} />
        {/if}
      </div>
    </div>
    <progress
      id="progress-{card['id']}"
      class="soundcard-progress soundcard-progress-off"
      value="0"
      max="100"
      bind:this={progressBar}
      on:pointerdown={handleProgressClicked}
      on:mousedown={handleProgressClicked}
    ></progress>
  </div>
</div>
